import React from "react";
import Navbar from "../../Components/common/Navbar";
import {
  Newspaper,
  CalendarDays,
  Globe2,
  Award,
  GraduationCap,
  Building2,
  ArrowRight,
  FileText,
} from "lucide-react";

export default function PressReleasePage() {
  const pressReleases = [
    {
      date: "June 11, 2026",
      category: "Platform Announcement",
      title: "WILEARN Expands Global Digital Learning Opportunities",
      description:
        "WILEARN continues to strengthen its global digital learning platform with accessible, flexible and practice-oriented programmes designed for learners, professionals, educators and communities worldwide.",
      icon: Globe2,
    },
    {
      date: "May 28, 2026",
      category: "Certification",
      title: "WILEARN Strengthens Digital Certification and Credential Recognition",
      description:
        "WILEARN is enhancing its digital certificate framework to support transparent learner achievement, professional development and digitally verifiable credentials across its learning ecosystem.",
      icon: Award,
    },
    {
      date: "May 10, 2026",
      category: "Professional Development",
      title: "New Professional Learning Pathways Introduced on WILEARN",
      description:
        "New structured learning pathways are being introduced across professional development, technology, leadership, education, sports, sustainability and emerging skill areas.",
      icon: GraduationCap,
    },
    {
      date: "April 22, 2026",
      category: "Institutional Development",
      title: "WILEARN Advances its Global Learning and Community Development Mission",
      description:
        "As an initiative of the World Council of Indigenous Games (WIG), WILEARN continues to promote accessible learning, cultural knowledge, skills development and professional education for a global learner community.",
      icon: Building2,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar page="about" />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-violet-950 via-violet-900 to-purple-800">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-violet-400 blur-3xl" />
          <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-purple-400 blur-3xl" />
          <div className="absolute bottom-0 left-1/2 h-72 w-72 rounded-full bg-fuchsia-300 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto max-w-5xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-violet-100 backdrop-blur-md">
              <Newspaper className="h-4 w-4" />
              WILEARN News & Announcements
            </div>

            <h1 className="mt-5 text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
              Press Release
            </h1>

            <p className="mx-auto mt-5 max-w-4xl text-lg leading-8 text-violet-100 sm:text-xl">
              Official announcements, platform updates, institutional
              developments and important news from WILEARN.
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.4fr_1fr] lg:gap-16">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
              Latest Updates
            </p>

            <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
              News from WILEARN
            </h2>

            <div className="mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500" />
          </div>

          <div>
            <p
              className="text-lg leading-8 text-slate-600"
              style={{ textAlign: "justify" }}
            >
              The WILEARN Press Release section provides official updates on
              platform developments, learning initiatives, certification,
              institutional activities, partnerships, professional education
              and global learning opportunities.
            </p>

            <p
              className="mt-5 text-lg leading-8 text-slate-600"
              style={{ textAlign: "justify" }}
            >
              This page serves as a central source of verified information for
              learners, trainers, institutions, organizations and other
              stakeholders interested in the continued development of the
              WILEARN learning ecosystem.
            </p>
          </div>
        </div>
      </section>

      {/* Press Release List */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
              Official Announcements
            </p>

            <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
              Recent Press Releases
            </h2>

            <p className="mt-5 text-lg leading-8 text-slate-600">
              Important announcements and updates from the WILEARN learning
              platform.
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-5xl space-y-6">
            {pressReleases.map(
              ({ date, category, title, description, icon: Icon }) => (
                <article
                  key={title}
                  className="group rounded-3xl border border-slate-200 bg-slate-50 p-6 transition duration-300 hover:-translate-y-1 hover:border-violet-300 hover:bg-white hover:shadow-lg sm:p-8"
                >
                  <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 transition group-hover:bg-violet-600 group-hover:text-white">
                      <Icon className="h-7 w-7" />
                    </div>

                    <div className="min-w-0 flex-1 text-left">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-violet-700">
                          {category}
                        </span>

                        <span className="flex items-center gap-2 text-sm text-slate-500">
                          <CalendarDays className="h-4 w-4" />
                          {date}
                        </span>
                      </div>

                      <h3 className="mt-4 text-2xl font-bold leading-snug text-slate-900 transition group-hover:text-violet-700">
                        {title}
                      </h3>

                      <p
                        className="mt-4 leading-8 text-slate-600"
                        style={{ textAlign: "justify" }}
                      >
                        {description}
                      </p>

                      <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-violet-700">
                       <FileText className="h-4 w-4" />
                       Read official update
                        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </article>
              )
            )}
          </div>
        </div>
      </section>

      {/* Media Information */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 rounded-3xl border border-violet-100 bg-violet-50 p-8 lg:grid-cols-[0.8fr_1.2fr] lg:p-10">
          <div>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-600 text-white">
              <Newspaper className="h-7 w-7" />
            </div>

            <h2 className="mt-6 text-3xl font-bold text-slate-900">
              Media & Institutional Enquiries
            </h2>
          </div>

          <div>
            <p
              className="text-lg leading-8 text-slate-700"
              style={{ textAlign: "justify" }}
            >
              For media enquiries, institutional information, collaboration
              requests or official clarification related to WILEARN, please use
              the Contact Us page to communicate with the appropriate team.
            </p>

            <p
              className="mt-5 leading-8 text-slate-600"
              style={{ textAlign: "justify" }}
            >
              Official information published through this page may be updated
              periodically to reflect current platform developments,
              announcements and institutional activities.
            </p>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-700 via-purple-700 to-violet-950 p-8 text-center shadow-xl sm:p-10 lg:p-14">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-fuchsia-300/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-violet-300/20 blur-3xl" />

          <div className="relative">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-200">
              WILEARN
            </p>

            <h2 className="mx-auto mt-3 max-w-4xl text-3xl font-bold text-white sm:text-4xl">
              Learning. Development. Recognition. Global Impact.
            </h2>

            <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-violet-100">
              Stay informed about the latest developments from the WILEARN
              global learning platform.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}